import { useEffect, useLayoutEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Button, Card, Icon, Pill } from '@iwoca/orion';
import Rive from '@rive-app/react-canvas';
import classnames from 'classnames';
import _ from 'lodash';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl
} from 'react-intl';
import { connect } from 'react-redux';

import { useGetSplitTestQuery } from '@lendingAPI/lendingAPIQueries';
import {
    getCountryCode,
    getRequestedAmount
} from '@lendingAPI/lendingAPISelector';
import { animations } from '@src/helpers/constants';
import { AppDispatch, RootState } from '@src/reducers';
import { getAmplitudeDefaultEventProperties } from '@src/tracking/amplitudeEvents';
import { displayCurrency } from '@src/utils/DisplayCurrency';
import { getSplitTestValue } from '@src/utils/selectors';

import {
    ACTIVATE_CHECK_ELIGIBILITY_PAGE,
    REDIRECT_TO_PERSONAL_INFORMATION
} from './CheckEligibility.actions';
import messages from './CheckEligibility.messages';
import styles from './CheckEligibility.module.css';

const mapStateToProps = (state: RootState) => ({
    requestedAmount: getRequestedAmount(state),
    annualTurnover: _.get(
        state,
        'form.sign_up.businessTypeFields.company.annual_turnover.value'
    ),
    countryCode: getCountryCode(state),
    isInSplitInterestOnly: getSplitTestValue(
        state,
        'signup_interest_only_test_2023'
    )
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
    activateCheckEligibilityView: () => {
        dispatch({ type: ACTIVATE_CHECK_ELIGIBILITY_PAGE });
    },
    redirectToPersonalInformation: () => {
        dispatch({ type: REDIRECT_TO_PERSONAL_INFORMATION });
    }
});

type TCheckEligibility = {
    annualTurnover: number;
    requestedAmount: number;
    redirectToPersonalInformation: () => void;
    activateCheckEligibilityView: () => void;
    countryCode: 'de' | 'uk';
    isInSplitInterestOnly: boolean;
};

type TBenefitSize = 'medium' | 'large';

const CheckEligibility = ({
    annualTurnover,
    requestedAmount,
    redirectToPersonalInformation,
    activateCheckEligibilityView,
    countryCode,
    isInSplitInterestOnly
}: TCheckEligibility & WrappedComponentProps) => {
    const [benefitsSize, setBenefitSize] = useState<TBenefitSize>('medium');
    const eligibleAmount = calculateEligibleAmount(
        requestedAmount,
        annualTurnover || 0
    );
    const { branch } = useGetSplitTestQuery('signup_no_pg_test');

    useEffect(() => {
        activateCheckEligibilityView();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (isInSplitInterestOnly || branch === 'include') {
            return setBenefitSize('large');
        }

        return setBenefitSize('medium');
    }, [isInSplitInterestOnly, branch]);

    useEffect(() => {
        tracking.ampli.checkEligibilityViewed({
            suggested_amount: eligibleAmount,
            requested_amount: requestedAmount
        });
    }, [countryCode, eligibleAmount, requestedAmount]);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleCTA = () => {
        tracking.ampli.checkEligibilityCtaClicked({
            ...getAmplitudeDefaultEventProperties(),
            ctaText: messages.cta.defaultMessage
        });

        return redirectToPersonalInformation();
    };

    return (
        <section className={styles.checkEligibilityContainer}>
            <div className={styles.animationContainer}>
                <Rive
                    src={animations.successTick}
                    className={styles.accountSuccessTickRive}
                    data-testid="success-tick-animation"
                />
            </div>
            <h1 className={styles.title}>
                {requestedAmount > eligibleAmount ? (
                    <FormattedMessage {...messages.title} />
                ) : (
                    <FormattedMessage
                        {...messages.suggestedAmountTitle}
                        values={{
                            suggestedAmount: displayCurrency(eligibleAmount)
                        }}
                    />
                )}
            </h1>
            <Card
                className={classnames(styles.benefitsCard, {
                    [styles.largeBenefitsContainer]: benefitsSize === 'large'
                })}>
                <section className={styles.benefitsContainer}>
                    {branch === 'include' && (
                        <div className={styles.benefit}>
                            <Pill variant="green" className={styles.noPGPill}>
                                <FormattedMessage {...messages.noPGPill} />
                            </Pill>
                            <p>
                                <FormattedMessage {...messages.benefitNoPG} />
                            </p>
                        </div>
                    )}
                    <div className={styles.benefit}>
                        <Icon
                            icon="starOutline"
                            className={styles.benefitIcon}
                        />
                        <p>
                            <FormattedMessage {...messages.benefitRepayEarly} />
                        </p>
                    </div>
                    <div className={styles.benefit}>
                        <Icon
                            icon="calendarOutline"
                            className={styles.benefitIcon}
                        />
                        <p>
                            <FormattedMessage {...messages.benefitBorrow} />
                        </p>
                    </div>
                    <div className={styles.benefit}>
                        <Icon icon="clock2" className={styles.benefitIcon} />
                        <p>
                            <FormattedMessage
                                {...messages.benefit24hourDecision}
                            />
                        </p>
                    </div>
                    {isInSplitInterestOnly && branch === 'exclude' && (
                        <div className={styles.benefit}>
                            <Icon
                                icon="exchange"
                                className={styles.benefitIcon}
                            />
                            <p>
                                <FormattedMessage
                                    {...messages.benefitInterestOnly}
                                />
                            </p>
                        </div>
                    )}
                </section>
                <p className={styles.terms}>
                    <small>
                        <FormattedMessage {...messages.terms} />
                    </small>
                </p>
                <p className={styles.ctaLabel}>
                    <strong>
                        <FormattedMessage {...messages.labelCta} />
                    </strong>
                </p>
                <Button onClick={handleCTA}>
                    <FormattedMessage {...messages.cta} />
                </Button>
            </Card>
        </section>
    );
};

const calculateEligibleAmount = (
    requestedAmount: number,
    annualTurnover: number
): number => {
    const tenthOfTurnover = Math.round(annualTurnover / 10);

    if (!requestedAmount) {
        return tenthOfTurnover;
    }

    return Math.min(requestedAmount, tenthOfTurnover);
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(CheckEligibility));
